import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useQuery, useMutation } from '@apollo/client';
import { ValidatorForm } from 'react-form-validator-core';

import PaymentCard from './PaymentCard';
import { GET_BILLING_INFO } from 'core/api/apollo/queries';
import { SET_BILLING_INFO } from 'core/api/apollo/mutations';
import { updateBillingInfoQuery, updateUserQuery } from 'core/api/cache/updaters';

import useAlerts from 'core/hooks/useAlerts';
import useErrorHandlers from 'core/hooks/useErrorHandlers';

import { Box, Flex, Text, ControlsGrid, FormGroup, Label, StripeCardInput, StripeExpiryInput, StripeCVCInput } from 'core/ui/atoms';
import StripeContext from 'core/ui/components/Stripe/Context';
import { ReactComponent as EditLine } from 'core/assets/images/V2/icons/EditLine.svg';
import { NavigationHeader } from 'onramp/ui/components';

import { SubmitButton } from 'settings/ui/atoms';
import ModalV2 from 'core/ui/components/V2/ModalV2';

const PaymentInfoV2 = ({ title }) => {
  const { loading, data: { getBillingInfo: billingInfo } = {} } = useQuery(GET_BILLING_INFO);
  const [modalOpen, setModalOpen] = useState(false);
  const alerts = useAlerts();

  const openModal = () => setModalOpen(true);

  const closeModal = () => setModalOpen(false);

  const onSuccess = () => {
    closeModal();
    alerts.show('Your changes have been saved!', { autoClose: true });
  };

  return (
    <Box loading={loading} data-payment-info>
      <Flex justifyContent='space-between'>
        {title && <HeaderText>{title}</HeaderText>}
        <EditLine onClick={openModal} />
      </Flex>
      <PaymentCard billingInfo={billingInfo} />

      <Box>
        { modalOpen && (
          <ModalV2 title='Credit Card' onClose={closeModal}>
            <Container>
              <NavigationHeader backNavigate={closeModal} title="Add Payment Details" />
              <StripeContext>
                <PaymentInfoForm
                  data-payment-info-form
                  onSuccess={onSuccess}
                />
              </StripeContext>
            </Container>
          </ModalV2>
        )}
      </Box>
    </Box>
  );
};

function PaymentInfoForm(props) {
  const [setBillingInfo] = useMutation(SET_BILLING_INFO, {
    update: (cache, { data: { setBillingInfo: { billingAccount, errors } } }) => {
      // NOTE: these are required to handle the case where the user didn't have a billing account
      //       when the app loaded
      updateUserQuery(cache, { user: { billingAccount } }, errors);
      updateBillingInfoQuery(cache, billingAccount, errors);
    },
  });
  const { displayApolloResponseErrors } = useErrorHandlers();
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [ready, setReady] = useState(false);

  const saveBillingInfo = (token) => {
    const { onSuccess } = props;

    setBillingInfo({ variables: { input: { stripeToken: token.id } } })
      .then(({ data: { setBillingInfo: { errors } } }) => {
        errors.length ? onServerError(errors) : onSuccess();
      })
      .catch(({ error }) => {
        onServerError(error);
      });
  };

  const onSubmit = (e) => {
    const { stripe, elements, getCardElement } = props;

    e.preventDefault();

    setLoading(true);
    setIsSubmitted(true);
    if (!stripe || !elements || !ready) {
      onError();
      return;
    }

    stripe
      .createToken(getCardElement())
      .then(({ error, token }) => {
        error ? onServerError(error) : saveBillingInfo(token);
      });
  };

  const onReady = () => setReady(true);

  const onError = () => {
    setLoading(false);
  };

  const onServerError = (error) => {
    onError();

    // NOTE: errors may contain either a single Stripe API error or an array of GraphQL API errors
    const errors = [error].flatten();
    displayApolloResponseErrors(errors);
  };

  return (
    <ValidatorForm
      onSubmit={onSubmit}
      onError={onError}
      noValidate
      instantValidate={false}
    >
      <FormGroup>
        <CustomLabel required>Card Number</CustomLabel>
        <StripeCardInput
          placeholder="ex. 4111 1111 1111 1111"
          onReady={onReady}
          showInvalidState={isSubmitted}
        />
      </FormGroup>
      <CustomControlsGrid>
        <FormGroup>
          <CustomLabel required>Exp date</CustomLabel>
          <StripeExpiryInput
            placeholder="ex. 11/23"
            showInvalidState={isSubmitted}
          />
        </FormGroup>
        <FormGroup>
          <CustomLabel required>CVC</CustomLabel>
          <StripeCVCInput
            placeholder="ex. 123"
            showInvalidState={isSubmitted}
          />
        </FormGroup>
      </CustomControlsGrid>

      <FooterButton direction='row' justifyContent='center'>
        <SaveButton disabled={loading}>SAVE & CONFIRM</SaveButton>
      </FooterButton>
    </ValidatorForm>
  );
}

PaymentInfoV2.propTypes = {
  title: PropTypes.string,
};

PaymentInfoForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  stripe: PropTypes.object,
  elements: PropTypes.object,
  getCardElement: PropTypes.func,
  stripeHelpers: PropTypes.object,
};

const FooterButton = styled(Flex)`
  margin-top: calc(10vh);
`;

const SaveButton = styled(SubmitButton)`
  border-radius: 4px;
  border-color: #C5D1C0;
  background: #C5D1C0;
  height: 52px;
  width: 90%;
  color: #36443A;
  text-align: center;
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: 1.5px;
`;

const Container = styled(Box)`
  padding-top: 50px;
`;

const CustomLabel = styled(Label)`
  padding: 0px;
  padding-bottom: 10px;
`;

const HeaderText = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  line-height: 22px;
  font-family: ${({ theme }) => theme.typography.familyLarken};
`;

const CustomControlsGrid = styled(ControlsGrid)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 28px;

  ${(p) => p.theme.max('sm')`
    grid-template-columns: 1fr;
    grid-gap: 0px;
  `};
`;

export default PaymentInfoV2;
